import React from "react"
import PropTypes from "prop-types"
import moment from "moment"
import isEqual from "lodash.isequal"

// components
import { Widget } from "quorumdesign"

import BarGrouped from "app/static/frontend/widgets/containers/visualization/BarGrouped"
import BarStacked from "app/static/frontend/widgets/containers/visualization/BarStacked"
import Line from "app/static/frontend/widgets/components/visualization/Line"
import Map from "app/static/frontend/widgets/containers/visualization/Map"
import Pie from "app/static/frontend/widgets/containers/visualization/Pie"

import { ErrorBoundary } from "app/static/frontend/components/ErrorBoundary"

// helpers
import { parseHtmlToReact } from "app/static/frontend/imports/desktopHelperFunctions"
import { getWidgetTransactionPermissions } from "shared/pac/permissionHelpers"

export class VisualizationWidget extends React.Component {
    constructor(props) {
        super(props)
        this.state = {
            data: null,
            loading: true,
        }
        this.mounted = false
    }

    componentDidMount() {
        this.mounted = true
        if (this.props.canLoad) {
            this.loadWidget()
        }
    }

    // When switching between visSubtypes, we do not unmount VisualizationWidget
    // because componentDidUpdate fires after render(), this leads to errors as
    // the data from a previous visualization (Bar, for example) can then be passed
    // to a rendered Pie, which can lead to errors since the Pie data is a different shape.

    // This ensures that we always clear the data whenever we select a new visSubtype value
    // (as in, we ensure that whenever the visSubtype prop changes
    // we clear data and timestamp before the next render() occurs)

    // getDerivedStateFromProps is invoked right before calling the render method,
    // both on the initial mount and on subsequent updates.
    // It should return an object to update the state, or null to update nothing.
    // This method exists for rare use cases where the state depends on changes in props over time.
    // reactjs.org/docs/react-component.html#static-getderivedstatefromprops

    // getDerivedStateFromProps is static to help ensure purity
    // (no access to instance properties),
    // which reduces risk when writing async-compatible React components.
    // https://twitter.com/dan_abramov/status/979745329649213440
    // https://stackoverflow.com/a/57950655/6201291
    // https://github.com/reactjs/rfcs/pull/6/files/b7189ccf42c5f8c039d268841efdfda296eec411#diff-5e06005dbc26fcac5989745eaca87e80R161
    static getDerivedStateFromProps(props, state) {
        // on initial load, set the state visSubtype/frequencyTupleField to the props
        // visSubtype/frequencyTupleField so we can diff them below
        if (props.editForm && !state.timestamp && !state.visSubtype) {
            // setState equivalent
            return {
                frequencyTupleField: props.frequencyTupleField,
                visSubtype: props.visSubtype,
            }
        }

        // if the visSubtype changes
        // (i.e., we select a new visualization type in the VisWidgetSidebarForm),
        // clear out the previous data/timestamp state
        if (props.editForm && props.visSubtype !== state.visSubtype) {
            // setState equivalent
            return {
                data: [],
                timestamp: undefined,
                visSubtype: props.visSubtype,
            }
        }

        // if the frequencyTupleField changes
        // (i.e., we select a new Group Data By (X-Axis) selection in the BarFormSection),
        // clear out the previous data/timestamp state
        if (props.editForm && props.frequencyTupleField !== state.frequencyTupleField) {
            // setState equivalent
            return {
                data: [],
                frequencyTupleField: props.frequencyTupleField,
                timestamp: undefined,
            }
        }

        return null
    }

    componentDidUpdate(prevProps) {
        if (
            this.props.canLoad &&
            ((!this.state.loading && this.state.data === null) ||
                (this.props.editForm &&
                    prevProps.widget &&
                    this.props.widget &&
                    !isEqual(prevProps.widget.data_sources, this.props.widget.data_sources)))
        ) {
            this.loadWidget()
            // [{data}] to [{empty data}] in editForm
        } else if (
            this.props.editForm &&
            this.state.timestamp &&
            prevProps.widget &&
            this.props.widget &&
            prevProps.widget.data_sources.length &&
            !this.props.canLoad
        ) {
            this.setState({
                data: [],
                timestamp: undefined,
            })
            // if we have selected data in the edit form
            // and select a new widget.meta.vis_options.cumulative
            // (which doesn't live in widget.data_sources and thus doesn't trigger this.loadWidget())
            // in the BarFormSection/LineFormSection VisWidgetSidebarForms, we want to
            // trigger a re-memo of the VisualizationWidget that has already rendered
        } else if (
            this.props.editForm &&
            this.state.timestamp &&
            prevProps.widget &&
            this.props.widget &&
            !isEqual(prevProps.widget.meta, this.props.widget.meta)
        ) {
            this.setState({
                timestamp: moment.now(),
            })
        }
    }

    componentWillUnmount() {
        this.mounted = false
    }

    loadWidget() {
        if (this.props.cachedData) {
            this.setState({
                data: this.props.cachedData,
                loading: false,
                timestamp: moment.now(),
            })

            return
        }

        this.setState({ loading: true })
        this.props.evaluateWidget(this.props.widget).then((response) => {
            if (this.mounted) {
                this.setState({
                    loading: false,
                    data: response.data,
                    timestamp: moment.now(),
                })
            }
        })
    }

    // checks to see if all data returned from loadWidget() is empty
    // if so, we want to unmount the widget and show the widgetFallback instead
    dataIsEmpty() {
        if (this.props.visSubtype === DjangIO.app.widgets.types.VisualizationSubtype.bar.value) {
            return Boolean(
                (Array.isArray(this.state.data) &&
                    // app/widgets/types.py CountVisualizationWidget
                    // [0, 0]
                    this.state.data.every((datum) => !datum) &&
                    // app/widgets/types.py GroupByWidget
                    // [{}, {}]
                    this.state.data.every((datum) => !Object.keys(datum).length)) ||
                    // 0
                    (!this.state.data &&
                        // {}
                        !Object.keys(this.state.data).length),
            )
        } else if (this.props.visSubtype === DjangIO.app.widgets.types.VisualizationSubtype.line.value) {
            return Boolean(
                (Array.isArray(this.state.data) &&
                    // app/widgets/types.py GroupByWidget
                    // [{}, {}]
                    this.state.data.every((datum) => !Object.keys(datum).length)) ||
                    // {}
                    !Object.keys(this.state.data).length,
            )
        } else if (this.props.visSubtype === DjangIO.app.widgets.types.VisualizationSubtype.pie.value) {
            return Boolean(
                // [0, 0]
                (Array.isArray(this.state.data) &&
                    // [0].find(item => item) = undefined
                    // [1].find(item => item) = 1
                    this.state.data.every((datum) => !datum)) ||
                    // 0
                    !this.state.data,
            )
        }

        return false
    }

    shouldOrderByDateTypes() {
        const current_data_type = this.props.dataSources[0].safed_search.data_type
        // limiting to Notes data type and date FrequencyTupleFields for now.
        return (
            current_data_type &&
            current_data_type === DjangIO.app.models.QuorumDataType.note.value &&
            DjangIO.app.widgets.types.FrequencyTupleFields.date_fields.includes(this.props.frequencyTupleField)
        )
    }

    renderVisualization() {
        switch (this.props.visSubtype) {
            case DjangIO.app.widgets.types.VisualizationSubtype.line.value:
                return (
                    <Line
                        colors={this.props.colors}
                        cumulative={this.props.cumulative}
                        data={this.state.data}
                        editing={this.props.editing}
                        editForm={this.props.editForm}
                        interval={this.props.interval}
                        labels={this.props.labels}
                        timestamp={this.state.timestamp}
                        widgetKey={this.props.widgetKey}
                    />
                )
            case DjangIO.app.widgets.types.VisualizationSubtype.map.value:
                return (
                    <Map
                        // needed to update values in the store when we click on a specific
                        // state or country in a FrequencyTupleFields.custom Map
                        change={this.props.change}
                        colors={this.props.colors}
                        data={this.state.data}
                        editing={this.props.editing}
                        editForm={this.props.editForm}
                        isExternal={this.props.isExternal}
                        timestamp={
                            // special case
                            // we need to manually generate a timestamp for the custom map since
                            // custom maps do not require any backend data to be evaluated by the widget_engine
                            this.props.frequencyTupleField ===
                            DjangIO.app.widgets.types.FrequencyTupleFields.custom.value
                                ? moment.now()
                                : this.state.timestamp
                        }
                        // timestamp={"timestamp"}
                        widget={this.props.widget}
                    />
                )
            case DjangIO.app.widgets.types.VisualizationSubtype.pie.value:
                return (
                    <Pie
                        colors={this.props.colors}
                        data={this.state.data}
                        editing={this.props.editing}
                        editForm={this.props.editForm}
                        isExternal={this.props.isExternal}
                        labels={this.props.labels}
                        timestamp={this.state.timestamp}
                        widget={this.props.widget}
                    />
                )
            case DjangIO.app.widgets.types.VisualizationSubtype.bar.value:
                switch (this.props.widget.meta.vis_options.bar_visualization_display_style) {
                    case DjangIO.app.widgets.types.BarVisualizationDisplayStyle.regular.value:
                        return (
                            <BarGrouped
                                shouldOrderByDate={this.shouldOrderByDateTypes()}
                                colors={this.props.colors}
                                data={this.state.data}
                                editing={this.props.editing}
                                editForm={this.props.editForm}
                                isExternal={this.props.isExternal}
                                labels={this.props.labels}
                                noHeader={!this.props.headerTitle}
                                timestamp={this.state.timestamp}
                                updateTimestamp={() => {
                                    this.setState({ timestamp: moment.now() })
                                }}
                                widget={this.props.widget}
                            />
                        )
                    case DjangIO.app.widgets.types.BarVisualizationDisplayStyle.stacked.value:
                        return (
                            <BarStacked
                                colors={this.props.colors}
                                data={this.state.data}
                                editing={this.props.editing}
                                editForm={this.props.editForm}
                                isExternal={this.props.isExternal}
                                shouldOrderByDate={this.shouldOrderByDateTypes()}
                                labels={this.props.labels}
                                noHeader={!this.props.headerTitle}
                                timestamp={this.state.timestamp}
                                updateTimestamp={() => {
                                    this.setState({ timestamp: moment.now() })
                                }}
                                widget={this.props.widget}
                            />
                        )
                    case DjangIO.app.widgets.types.BarVisualizationDisplayStyle.one_hundred_percent_stacked.value:
                    default:
                        return (
                            <BarStacked
                                colors={this.props.colors}
                                data={this.state.data}
                                editing={this.props.editing}
                                editForm={this.props.editForm}
                                isExternal={this.props.isExternal}
                                shouldOrderByDate={this.shouldOrderByDateTypes()}
                                labels={this.props.labels}
                                noHeader={!this.props.headerTitle}
                                percentage
                                timestamp={this.state.timestamp}
                                updateTimestamp={() => {
                                    this.setState({ timestamp: moment.now() })
                                }}
                                widget={this.props.widget}
                            />
                        )
                }
            default:
                return <div>This Visualization type is not supported yet!</div>
        }
    }

    render() {
        // Get widget transaction permissions using the centralized helper
        const { isForbidden } = getWidgetTransactionPermissions(this.props.qdt)

        return (
            <Widget
                description={
                    // helper function that will convert a raw HTML string -> DOM Nodes -> React JSX
                    this.props.description && parseHtmlToReact(this.props.description, this.props.widgetKey, false)
                }
                editing={this.props.editing}
                emptyPlaceholder={this.props.emptyPlaceholder}
                forbiddenMessage="You cannot view transaction widgets"
                headerIcons={this.props.headerIcons}
                headerTitle={this.props.headerTitle}
                id={this.props.widgetKey && `widget-${this.props.widgetKey}`}
                isForbidden={isForbidden}
                noHeader={!this.props.headerTitle}
                visualization
                widget={
                    // special case
                    // custom maps do not require any backend data to be evaluated by the widget_engine
                    ((this.props.visSubtype === DjangIO.app.widgets.types.VisualizationSubtype.map.value &&
                        this.props.frequencyTupleField ===
                            DjangIO.app.widgets.types.FrequencyTupleFields.custom.value) ||
                        (!this.state.loading && this.state.timestamp && this.state.data && !this.dataIsEmpty())) && (
                        <ErrorBoundary errorMessage="Failed to render widget">
                            {this.renderVisualization()}
                        </ErrorBoundary>
                    )
                }
                widgetContentIsLoading={
                    // special case
                    // custom maps do not require any backend data to be evaluated by the widget_engine
                    !(
                        this.props.visSubtype === DjangIO.app.widgets.types.VisualizationSubtype.map.value &&
                        this.props.frequencyTupleField === DjangIO.app.widgets.types.FrequencyTupleFields.custom.value
                    ) && this.state.loading
                }
                widgetFallback={
                    // if we have a widget, but it has not loaded yet
                    // show the loading spinner
                    (this.props.canLoad &&
                        // (!this.state.data || !this.state.data.length) &&
                        this.state.loading &&
                        "Loading...") ||
                    (!this.state.loading &&
                        this.state.timestamp &&
                        this.state.data &&
                        this.dataIsEmpty() &&
                        "Your Selection of Filter(s) Returned No Data")
                }
            />
        )
    }
}

VisualizationWidget.defaultProps = {}
VisualizationWidget.propTypes = {
    cachedData: PropTypes.object,
    canLoad: PropTypes.bool,
    colors: PropTypes.object,
    cumulative: PropTypes.bool,
    description: PropTypes.string,
    editForm: PropTypes.bool,
    editing: PropTypes.bool,
    emptyPlaceholder: PropTypes.string,
    evaluateWidget: PropTypes.func,
    headerIcons: PropTypes.array,
    headerTitle: PropTypes.string,
    interval: PropTypes.number,
    isExternal: PropTypes.bool,
    labels: PropTypes.arrayOf(PropTypes.string),
    qdt: PropTypes.object,
    visSubtype: PropTypes.number,
    widget: PropTypes.object,
    widgetKey: PropTypes.string,

    // bar
    frequencyTupleField: PropTypes.number,

    // from redux-form (see https://redux-form.com/8.1.0/docs/api/props.md/)
    // needed to update values in the store when we click on a specific state or country in the Map
    change: PropTypes.func,
}

export default VisualizationWidget
